import { supabase } from "@/shared/lib/supabaseClient";
import type { Database } from "@/shared/types/database.types";

/**
 * Camada de dados das Notificações (ARQ-04, migration 0034).
 *
 * Antes, o único feed do sino eram convites de ministério (DA-003). Esta
 * tabela + os triggers de banco (`notificar_status_reserva`,
 * `notificar_reserva_de_evento`) fecham o BUG-03 (evento sincroniza reserva
 * sem avisar quem administra o módulo Reservas) e o BUG-05 (quem solicitou
 * uma reserva não sabia se foi aprovada/rejeitada, nem o motivo).
 */
export type Notificacao = Database["public"]["Tables"]["notificacoes"]["Row"];

export const notificacoesService = {
  /** Últimas notificações da pessoa logada (lidas e não lidas, mais recentes primeiro). */
  async listar(pessoaId: string): Promise<Notificacao[]> {
    const { data, error } = await supabase
      .from("notificacoes")
      .select("*")
      .eq("pessoa_id", pessoaId)
      .order("created_at", { ascending: false })
      .limit(30);
    if (error) throw new Error("Não foi possível carregar as notificações.");
    return data ?? [];
  },

  async marcarComoLida(id: string): Promise<void> {
    const { error } = await supabase.from("notificacoes").update({ lida: true }).eq("id", id);
    if (error) throw new Error("Não foi possível marcar a notificação como lida.");
  },

  async marcarTodasComoLidas(pessoaId: string): Promise<void> {
    const { error } = await supabase
      .from("notificacoes")
      .update({ lida: true })
      .eq("pessoa_id", pessoaId)
      .eq("lida", false);
    if (error) throw new Error("Não foi possível marcar as notificações como lidas.");
  },

  /** A32 (reteste 22-23/07/2026): apaga uma notificação da pessoa logada. */
  async apagar(id: string): Promise<void> {
    const { error } = await supabase.from("notificacoes").delete().eq("id", id);
    if (error) throw new Error("Não foi possível apagar a notificação.");
  },

  /**
   * "Excluir todas" do sino: apaga TODAS as notificações da pessoa logada, inclusive as mais
   * antigas que a lista (30 mais recentes) não mostra. A RLS (`notificacoes_delete`) já limita a
   * pessoa dona; o filtro por `pessoa_id` aqui é só para deixar a intenção explícita.
   * Convites de ministério e cadastros pendentes não moram nesta tabela, então não são afetados.
   */
  async apagarTodas(pessoaId: string): Promise<void> {
    const { error } = await supabase.from("notificacoes").delete().eq("pessoa_id", pessoaId);
    if (error) throw new Error("Não foi possível excluir as notificações.");
  },
};
